import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from "react";
import {
  api,
  type CzasotekstFramePreset,
  type CzasotekstOverlay,
  type CzasotekstOverlayBox,
  type CzasotekstOverlayText,
  type MediaAsset,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const CANVAS_W = 1080;
const CANVAS_H = 1920;
const PREVIEW_W = 270;
const MIN_W = 160;
const MIN_H = 100;
const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const;
type Handle = (typeof HANDLES)[number];

const DEFAULT_BOX: CzasotekstOverlayBox = { x: 80, y: 720, w: 920, h: 280, rotate: 0 };
const DEFAULT_TEXT: CzasotekstOverlayText = {
  font: "SourceSans3-Bold",
  size: 64,
  color: "#f8f5ef",
  align: "center",
};

function clampBox(box: CzasotekstOverlayBox): CzasotekstOverlayBox {
  let x = Number(box.x || 0);
  let y = Number(box.y || 0);
  let w = Math.max(MIN_W, Number(box.w || MIN_W));
  let h = Math.max(MIN_H, Number(box.h || MIN_H));
  if (x < 0) {
    w += x;
    x = 0;
  }
  if (y < 0) {
    h += y;
    y = 0;
  }
  if (x + w > CANVAS_W) w = CANVAS_W - x;
  if (y + h > CANVAS_H) h = CANVAS_H - y;
  return {
    x: Math.round(x),
    y: Math.round(y),
    w: Math.max(MIN_W, Math.round(w)),
    h: Math.max(MIN_H, Math.round(h)),
    rotate: Number(box.rotate || 0),
  };
}

function resizeFromHandle(
  start: { handle?: Handle; ox: number; oy: number; ow: number; oh: number },
  dx: number,
  dy: number,
): CzasotekstOverlayBox {
  let x = start.ox;
  let y = start.oy;
  let w = start.ow;
  let h = start.oh;
  const handle = start.handle || "se";
  if (handle.includes("e")) w = start.ow + dx;
  if (handle.includes("s")) h = start.oh + dy;
  if (handle.includes("w")) {
    x = start.ox + dx;
    w = start.ow - dx;
  }
  if (handle.includes("n")) {
    y = start.oy + dy;
    h = start.oh - dy;
  }
  return clampBox({ x, y, w, h, rotate: 0 });
}

function normalizeOverlay(raw?: CzasotekstOverlay | null): Required<CzasotekstOverlay> {
  const box = clampBox({ ...DEFAULT_BOX, ...(raw?.box || {}) });
  const text = { ...DEFAULT_TEXT, ...(raw?.text || {}) };
  return {
    frame_id: String(raw?.frame_id || "pill_dark"),
    box,
    text,
  };
}

function handleStyle(handle: Handle): CSSProperties {
  const common: CSSProperties = {
    position: "absolute",
    width: 10,
    height: 10,
    background: "#fff",
    border: "1px solid #b5614e",
    borderRadius: 2,
    zIndex: 3,
  };
  const map: Record<Handle, CSSProperties> = {
    nw: { ...common, left: -5, top: -5, cursor: "nwse-resize" },
    n: { ...common, left: "50%", top: -5, marginLeft: -5, cursor: "ns-resize" },
    ne: { ...common, right: -5, top: -5, cursor: "nesw-resize" },
    e: { ...common, right: -5, top: "50%", marginTop: -5, cursor: "ew-resize" },
    se: { ...common, right: -5, bottom: -5, cursor: "nwse-resize" },
    s: { ...common, left: "50%", bottom: -5, marginLeft: -5, cursor: "ns-resize" },
    sw: { ...common, left: -5, bottom: -5, cursor: "nesw-resize" },
    w: { ...common, left: -5, top: "50%", marginTop: -5, cursor: "ew-resize" },
  };
  return map[handle];
}

export default function CzasotekstComposeCanvas({
  frames,
  overlay: overlayProp,
  hook,
  videoUrl,
  videoAsset,
  scalePan,
  onChange,
  onSave,
  onCompose,
  saving,
  composing,
  ffmpegOk,
}: {
  frames: CzasotekstFramePreset[];
  overlay?: CzasotekstOverlay | null;
  hook: string;
  videoUrl?: string;
  videoAsset?: MediaAsset | null;
  scalePan: { scale: number; x: number; y: number };
  onChange: (next: CzasotekstOverlay) => void;
  onSave: () => void;
  onCompose: () => void;
  saving?: boolean;
  composing?: boolean;
  ffmpegOk?: boolean;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [overlay, setOverlay] = useState(() => normalizeOverlay(overlayProp));
  const drag = useRef<{
    kind: "move" | "resize";
    handle?: Handle;
    sx: number;
    sy: number;
    ox: number;
    oy: number;
    ow: number;
    oh: number;
  } | null>(null);

  useEffect(() => {
    setOverlay(normalizeOverlay(overlayProp));
  }, [overlayProp]);

  const previewScale = PREVIEW_W / CANVAS_W;
  const frame = useMemo(
    () => frames.find((row) => row.id === overlay.frame_id) || frames[0],
    [frames, overlay.frame_id],
  );

  function emit(next: Required<CzasotekstOverlay>) {
    setOverlay(next);
    onChange(next);
  }

  function toCanvas(clientX: number, clientY: number) {
    const el = stageRef.current;
    if (!el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return {
      x: ((clientX - r.left) / r.width) * CANVAS_W,
      y: ((clientY - r.top) / r.height) * CANVAS_H,
    };
  }

  function onBoxDown(e: PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = toCanvas(e.clientX, e.clientY);
    drag.current = {
      kind: "move",
      sx: p.x,
      sy: p.y,
      ox: overlay.box.x,
      oy: overlay.box.y,
      ow: overlay.box.w,
      oh: overlay.box.h,
    };
  }

  function onResizeDown(e: PointerEvent<HTMLButtonElement>, handle: Handle) {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = toCanvas(e.clientX, e.clientY);
    drag.current = {
      kind: "resize",
      handle,
      sx: p.x,
      sy: p.y,
      ox: overlay.box.x,
      oy: overlay.box.y,
      ow: overlay.box.w,
      oh: overlay.box.h,
    };
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    const p = toCanvas(e.clientX, e.clientY);
    const dx = p.x - drag.current.sx;
    const dy = p.y - drag.current.sy;
    if (drag.current.kind === "resize") {
      const nextBox = resizeFromHandle(drag.current, dx, dy);
      emit({ ...overlay, box: nextBox });
      return;
    }
    emit({
      ...overlay,
      box: clampBox({
        ...overlay.box,
        x: drag.current.ox + dx,
        y: drag.current.oy + dy,
      }),
    });
  }

  function onPointerUp() {
    drag.current = null;
  }

  const box = overlay.box;
  const ink = overlay.text?.color || frame?.ink || "#f8f5ef";
  const rawBg = frame?.bg || "#281f1c";
  const bg = rawBg.length === 9 ? rawBg.slice(0, 7) : rawBg === "transparent" || rawBg.endsWith("00") ? "#281f1c" : rawBg.slice(0, 7);
  const pad = Number(frame?.padding || 16);
  const radius = Number(frame?.radius || 0);
  const fontSize = Number(overlay.text?.size || 64) * previewScale;
  const align = overlay.text?.align || "center";

  return (
    <div className="grid gap-3 lg:grid-cols-[auto_minmax(0,1fr)]">
      <div className="space-y-2">
        <div
          ref={stageRef}
          className="relative overflow-hidden rounded border bg-black shadow-sm select-none"
          style={{ width: PREVIEW_W, height: PREVIEW_W * (CANVAS_H / CANVAS_W) }}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {videoUrl ? (
            <video
              key={videoAsset?.id || videoUrl}
              className="absolute inset-0 h-full w-full object-cover"
              src={videoUrl}
              muted
              playsInline
              loop
              autoPlay
              style={{
                transform: `scale(${scalePan.scale}) translate(${scalePan.x / scalePan.scale}px, ${scalePan.y / scalePan.scale}px)`,
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center bg-zinc-900 text-[11px] text-zinc-400">
              Wybierz film
            </div>
          )}
          <div
            className="absolute cursor-move"
            style={{
              left: box.x * previewScale,
              top: box.y * previewScale,
              width: box.w * previewScale,
              height: box.h * previewScale,
              transform: box.rotate ? `rotate(${box.rotate}deg)` : undefined,
              background: bg,
              borderRadius: radius * previewScale,
              border:
                frame?.border && frame?.border_w
                  ? `${Math.max(1, Number(frame.border_w) * previewScale)}px solid ${frame.border}`
                  : "1px solid rgba(255,255,255,0.35)",
              boxShadow: frame?.shadow
                ? frame.shadow_hard
                  ? "3px 3px 0 rgba(0,0,0,0.85)"
                  : "0 2px 8px rgba(0,0,0,0.45)"
                : undefined,
              padding: pad * previewScale,
              color: ink,
              fontSize,
              lineHeight: 1.2,
              fontWeight: 700,
              textAlign: align as "left" | "center" | "right",
              display: "flex",
              alignItems: "center",
              justifyContent: align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center",
              textShadow: frame?.panel === false ? "0 1px 3px rgba(0,0,0,0.85)" : undefined,
            }}
            onPointerDown={onBoxDown}
          >
            <span className="w-full break-words">{hook.trim() || "Hak na ekranie"}</span>
            {HANDLES.map((handle) => (
              <button
                key={handle}
                type="button"
                aria-label={`resize-${handle}`}
                style={handleStyle(handle)}
                onPointerDown={(e) => onResizeDown(e, handle)}
              />
            ))}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Podgląd 9:16. Przeciągnij hak i zmień rozmiar uchwytami. Pełny opis zostaje w caption.
        </p>
      </div>

      <div className="min-w-0 space-y-3">
        <div>
          <p className="mb-1 text-[11px] font-medium text-slate-700">Ramka haka (20 stylów)</p>
          <div className="grid max-h-56 grid-cols-2 gap-1.5 overflow-auto sm:grid-cols-3 xl:grid-cols-4">
            {frames.map((row) => (
              <button
                key={row.id}
                type="button"
                className={cn(
                  "rounded border px-1.5 py-1.5 text-left text-[10px] leading-tight",
                  overlay.frame_id === row.id ? "border-brand bg-brand/5" : "border-border hover:bg-muted/40",
                )}
                onClick={() => emit({ ...overlay, frame_id: row.id })}
              >
                <span
                  className="mb-1 block h-6 w-full rounded"
                  style={{
                    background: (row.bg || "#281f1c").slice(0, 7),
                    border: row.border ? `1px solid ${row.border}` : undefined,
                    color: row.ink || "#fff",
                    fontSize: 9,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  Aa
                </span>
                {row.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-[11px]">
            Rozmiar tekstu {overlay.text?.size || 64}
            <input
              type="range"
              min={32}
              max={96}
              step={2}
              value={overlay.text?.size || 64}
              className="w-full"
              onChange={(e) =>
                emit({
                  ...overlay,
                  text: { ...overlay.text, size: Number(e.target.value) },
                })
              }
            />
          </label>
          <label className="text-[11px]">
            Kolor
            <input
              type="color"
              className="ml-2 h-7 w-12 align-middle"
              value={(overlay.text?.color || "#f8f5ef").slice(0, 7)}
              onChange={(e) =>
                emit({
                  ...overlay,
                  text: { ...overlay.text, color: e.target.value },
                })
              }
            />
          </label>
          <label className="text-[11px]">
            Wyrównanie
            <select
              className="mt-0.5 w-full rounded border px-1.5 py-1 text-xs"
              value={overlay.text?.align || "center"}
              onChange={(e) =>
                emit({
                  ...overlay,
                  text: { ...overlay.text, align: e.target.value },
                })
              }
            >
              <option value="left">Lewo</option>
              <option value="center">Środek</option>
              <option value="right">Prawo</option>
            </select>
          </label>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={saving} onClick={onSave}>
            {saving ? "Zapisuję…" : "Zapisz skład ekranu"}
          </Button>
          <Button size="sm" variant="brand" disabled={composing || ffmpegOk === false} onClick={onCompose}>
            {composing ? "Składam MP4…" : "Złóż czasotekst (MP4)"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Prefetch helper: keep import of api used for media URLs in parent. */
export function czasotekstVideoUrl(assetId: string) {
  return api.mediaFile(assetId, false);
}
